import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import { RecruitGroupResponseDto, RecruitGroupShareReviewDto, RecruitGroupShareViewDto } from '@/app/common/general-types/recruit-groups.dto';
import {
  CREATOR_REVIEW_FILTERS,
  CreatorReviewFilter,
  CreatorReviewStatus,
  creatorReviewFilterCounts,
  creatorReviewFilterEmptyCopy,
  defaultCreatorReviewFilter,
  embedRecruitGroupReview,
  hydrateRecruitGroupReview,
  normalizeReviewerFirstName,
  normalizeReviewNote,
  parseRecruitGroupReview,
  resolveCreatorReviewStatus,
  REVIEWER_NAME_MAX_LENGTH,
  visibleGroupDescription,
} from '@/app/common/general-types/recruit-groups.review';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { ShareReviewCreator, ShareReviewFeedback } from './molecules/share-review-creator/share-review-creator';
import { RecruitGroupService } from '@/app/services/recruit-group/recruit-group.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { CreatorProfileComponent } from '@/app/pages/creators/molecules/creator-profile/creator-profile.component';
import { MagicSearchService } from '@/app/pages/creators/service/magic-search/magic-search.service';
import { SingleVideoViewerSummaryComponent } from '@/app/atoms/single-video-viewer-summary/single-video-viewer-summary.component';

const REVIEWER_NAME_STORAGE_PREFIX = 'shout-share-reviewer-name:';

@Component({
  selector: 'app-group-share',
  imports: [ShareReviewCreator, CreatorProfileComponent, SingleVideoViewerSummaryComponent],
  templateUrl: './group-share.html',
  styleUrl: './group-share.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupShare implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly recruitGroupService = inject(RecruitGroupService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly toastNotificationService = inject(ToastNotificationService);
  readonly magicSearchService = inject(MagicSearchService);

  private readonly subscriptionList = new Subscription();
  private reviewSaveId = 0;
  private inviteQueued = false;

  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  readonly group = signal<RecruitGroupResponseDto | undefined>(undefined);
  readonly creators = signal<CreatorResponseDto[]>([]);
  readonly brandName = signal<string | undefined>(undefined);
  readonly shareToken = signal('');
  readonly droppedIds = signal<Set<string>>(new Set());
  readonly keptIds = signal<Set<string>>(new Set());
  readonly creatorNotes = signal<Record<string, string>>({});
  readonly reviewerName = signal('');
  readonly nameNeeded = signal(false);
  readonly reviewerNameMaxLength = REVIEWER_NAME_MAX_LENGTH;
  readonly reviewFilter = signal<CreatorReviewFilter>('all');
  readonly inviting = signal(false);
  readonly inviteBriefTitle = signal<string | undefined>(undefined);
  readonly invitedIds = signal<Set<string>>(new Set());

  readonly canWrite = computed(() => this.loadingState() === 'loaded');

  readonly canInviteApproved = computed(() =>
    Boolean(
      this.group()?.allowReviewerInvite
      && (this.inviteBriefTitle() || this.group()?.reviewerInviteBriefId),
    ),
  );

  readonly approvedForInviteCount = computed(() =>
    this.creators().filter((creator) =>
      this.isApproved(creator) && !this.isInvited(creator),
    ).length,
  );

  readonly approvedCount = computed(() =>
    this.creators().filter((creator) => this.creatorReviewStatus(creator) === 'approved').length,
  );

  readonly reviewedCount = computed(() =>
    this.creators().filter((creator) => this.creatorReviewStatus(creator) !== 'pending').length,
  );

  readonly reviewProgressPercent = computed(() => {
    const total = this.creators().length;
    if (total === 0) {
      return 0;
    }
    return Math.round((this.reviewedCount() / total) * 100);
  });

  readonly reviewFilterTabs = computed(() => {
    const counts = creatorReviewFilterCounts(
      this.creators().map((creator) => this.creatorReviewStatus(creator)),
    );
    return CREATOR_REVIEW_FILTERS.map((tab) => ({
      ...tab,
      count: counts[tab.id],
    }));
  });

  readonly emptyFilterCopy = computed(() => creatorReviewFilterEmptyCopy(this.reviewFilter()));

  readonly pendingCount = computed(() =>
    this.creators().filter((creator) => this.creatorReviewStatus(creator) === 'pending').length,
  );

  readonly pendingReviewTitle = computed(() => {
    const count = this.pendingCount();
    if (count === 0) {
      return "You're all caught up!";
    }
    return count === 1 ? '1 creator to review' : `${count} creators to review`;
  });

  readonly visibleCreators = computed(() => {
    const creators = this.creators();
    const filter = this.reviewFilter();
    if (filter === 'all') {
      return creators;
    }
    return creators.filter((creator) => this.creatorReviewStatus(creator) === filter);
  });

  readonly visibleDescription = computed(() => visibleGroupDescription(this.group()?.groupDescription));

  ngOnInit(): void {
    this.subscriptionList.add(
      this.route.paramMap.subscribe((params) => {
        const token = params.get('token') ?? '';
        this.shareToken.set(token);
        this.reviewerName.set(this.readStoredReviewerName(token) || this.loggedInFirstName());
        this.nameNeeded.set(false);
        this.loadShare(token);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  isDeclined(creator: CreatorResponseDto): boolean {
    return this.droppedIds().has(creator.blazeFirebaseUid);
  }

  isApproved(creator: CreatorResponseDto): boolean {
    const id = creator.blazeFirebaseUid;
    return this.keptIds().has(id) && !this.droppedIds().has(id);
  }

  isInvited(creator: CreatorResponseDto): boolean {
    return this.invitedIds().has(creator.blazeFirebaseUid);
  }

  creatorReviewStatus(creator: CreatorResponseDto): CreatorReviewStatus {
    return resolveCreatorReviewStatus({
      declined: this.isDeclined(creator),
      approved: this.isApproved(creator),
      hasNote: Boolean(this.creatorNote(creator)),
      groupApproved: this.group()?.approvalStatus === 'approved',
    });
  }

  setReviewFilter(filter: CreatorReviewFilter): void {
    this.reviewFilter.set(filter);
  }

  isReviewFilterSelected(filter: CreatorReviewFilter): boolean {
    return this.reviewFilter() === filter;
  }

  reviewFilterButtonClass(filter: CreatorReviewFilter): string {
    const selected = this.isReviewFilterSelected(filter);
    const base = 'inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';
    if (!selected) {
      return `${base} text-secondary hover:bg-white hover:text-primary dark:hover:bg-zinc-700`;
    }
    if (filter === 'pending') {
      return `${base} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200`;
    }
    if (filter === 'approved') {
      return `${base} bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200`;
    }
    if (filter === 'declined') {
      return `${base} bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200`;
    }
    if (filter === 'feedback') {
      return `${base} bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200`;
    }
    return `${base} bg-white text-primary shadow-sm dark:bg-zinc-700 dark:text-white`;
  }

  reviewLabel(creator: CreatorResponseDto): string | undefined {
    if (this.isDeclined(creator)) {
      const name = this.group()?.droppedByName?.trim();
      return name ? `Declined by ${name}` : 'Declined';
    }
    if (this.isApproved(creator)) {
      const name = this.group()?.keptByName?.trim();
      return name ? `Approved by ${name}` : 'Approved';
    }
    if (this.creatorNote(creator)) {
      const name = this.normalizedReviewerName()
        || this.group()?.droppedByName?.trim()
        || this.group()?.keptByName?.trim()
        || this.group()?.approvedByName?.trim();
      return name ? `Feedback from ${name}` : 'Feedback';
    }
    if (this.group()?.approvalStatus === 'approved') {
      const name = this.group()?.approvedByName?.trim();
      return name ? `Approved by ${name}` : 'Approved';
    }
    return undefined;
  }

  onReviewerNameInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value.slice(0, REVIEWER_NAME_MAX_LENGTH);
    this.reviewerName.set(value);
    if (normalizeReviewerFirstName(value)) {
      this.nameNeeded.set(false);
    }
    this.storeReviewerName(this.shareToken(), value);
  }

  onApprove(creator: CreatorResponseDto): void {
    if (!this.ensureReviewerName()) {
      return;
    }
    this.applyDecision(creator, 'approve');
    this.persistReviewState(true);
  }

  onDecline(creator: CreatorResponseDto): void {
    if (!this.ensureReviewerName()) {
      return;
    }
    this.applyDecision(creator, 'decline');
    this.persistReviewState();
  }

  onReviewFeedback(creator: CreatorResponseDto, payload: ShareReviewFeedback): void {
    if (!this.ensureReviewerName()) {
      return;
    }
    const normalized = normalizeReviewNote(payload.note);
    this.creatorNotes.update((current) => {
      const next = { ...current };
      if (normalized) {
        next[creator.blazeFirebaseUid] = normalized;
      } else {
        delete next[creator.blazeFirebaseUid];
      }
      return next;
    });
    this.applyDecision(creator, payload.decision);
    this.persistReviewState();
  }

  creatorNote(creator: CreatorResponseDto): string | undefined {
    return this.creatorNotes()[creator.blazeFirebaseUid];
  }

  onCreatorNote(creator: CreatorResponseDto, note: string): void {
    if (!this.ensureReviewerName()) {
      return;
    }
    const normalized = normalizeReviewNote(note);
    this.creatorNotes.update((current) => {
      const next = { ...current };
      if (normalized) {
        next[creator.blazeFirebaseUid] = normalized;
      } else {
        delete next[creator.blazeFirebaseUid];
      }
      return next;
    });
    this.persistReviewState();
  }

  closeVideoPreview(): void {
    this.magicSearchService.previewVideo.set(undefined);
    this.magicSearchService.previewVideoPlaylist.set(undefined);
  }

  inviteApprovedCreators(): void {
    const token = this.shareToken();
    if (!token || !this.canInviteApproved() || this.approvedForInviteCount() === 0) {
      return;
    }
    if (this.inviting()) {
      this.inviteQueued = true;
      return;
    }

    this.inviting.set(true);
    this.subscriptionList.add(
      this.recruitGroupService.inviteApprovedFromShare(token).subscribe({
        next: (result) => {
          this.inviting.set(false);
          this.invitedIds.update((current) => {
            const next = new Set(current);
            const invited = result.invitedCreatorIds?.length
              ? result.invitedCreatorIds
              : this.creators()
                .filter((creator) => this.creatorReviewStatus(creator) === 'approved')
                .map((creator) => creator.blazeFirebaseUid);
            for (const id of invited) {
              next.add(id);
            }
            return next;
          });
          if (result.invitedCount > 0) {
            void this.toastNotificationService.alert({
              title: 'Creators invited',
              message: `${result.invitedCount} approved creator${result.invitedCount === 1 ? '' : 's'} invited to ${result.briefTitle}.`,
              type: 'success',
              icon: 'success',
            });
          }
          if (this.inviteQueued) {
            this.inviteQueued = false;
            this.inviteApprovedCreators();
          }
        },
        error: () => {
          this.inviting.set(false);
          this.inviteQueued = false;
          void this.toastNotificationService.alert({
            title: 'Could not send invites',
            message: 'The creator was approved, but we could not invite them to the brief. Refresh this page to try again.',
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  private applyDecision(creator: CreatorResponseDto, decision: 'approve' | 'decline' | 'feedback'): void {
    const id = creator.blazeFirebaseUid;
    if (decision === 'feedback') {
      this.droppedIds.update((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
      this.keptIds.update((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
      return;
    }
    if (decision === 'approve') {
      this.droppedIds.update((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
      this.keptIds.update((current) => {
        const next = new Set(current);
        next.add(id);
        return next;
      });
      return;
    }
    this.keptIds.update((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
    this.droppedIds.update((current) => {
      const next = new Set(current);
      next.add(id);
      return next;
    });
  }

  private persistReviewState(inviteApproved = false): void {
    const group = this.group();
    const token = this.shareToken();
    if (!group || !token) {
      return;
    }

    const name = this.normalizedReviewerName();
    const droppedCreatorIds = [...this.droppedIds()];
    const keptCreatorIds = [...this.keptIds()].filter((id) => !this.droppedIds().has(id));
    const review = parseRecruitGroupReview(group);
    const nextReview = {
      droppedCreatorIds,
      droppedByName: droppedCreatorIds.length > 0 ? name : group.droppedByName,
      keptCreatorIds,
      keptByName: keptCreatorIds.length > 0 ? name : group.keptByName,
      approvedByName: group.approvedByName ?? review.approvedByName,
      creatorNotes: this.creatorNotes(),
      allowReviewerInvite: group.allowReviewerInvite ?? review.allowReviewerInvite,
      reviewerInviteBriefId: group.reviewerInviteBriefId ?? review.reviewerInviteBriefId,
      invitedCreatorIds: [...this.invitedIds()].length
        ? [...this.invitedIds()]
        : (group.invitedCreatorIds ?? review.invitedCreatorIds),
    };
    const next = hydrateRecruitGroupReview({
      ...group,
      ...nextReview,
      groupDescription: embedRecruitGroupReview(group.groupDescription, nextReview),
    });
    this.group.set(next);

    const saveId = ++this.reviewSaveId;
    this.subscriptionList.add(
      this.recruitGroupService.reviewSharedGroup(token, this.toShareReview(next)).subscribe({
        next: (view) => {
          if (saveId !== this.reviewSaveId || this.shareToken() !== token) {
            return;
          }
          this.applyLoadedView(view);
          if (inviteApproved) {
            this.inviteApprovedCreators();
          }
        },
        error: () => {
          if (saveId !== this.reviewSaveId || this.shareToken() !== token) {
            return;
          }
          void this.toastNotificationService.alert({
            title: 'Could not save review',
            message: 'Your review is on this page. Try again to save it for the brand.',
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  private ensureReviewerName(): boolean {
    const name = this.normalizedReviewerName();
    if (name) {
      this.reviewerName.set(name);
      this.storeReviewerName(this.shareToken(), name);
      this.nameNeeded.set(false);
      return true;
    }
    this.nameNeeded.set(true);
    this.focusReviewerName();
    return false;
  }

  private normalizedReviewerName(): string {
    return normalizeReviewerFirstName(this.reviewerName());
  }

  private toShareReview(group: RecruitGroupResponseDto): RecruitGroupShareReviewDto {
    return {
      groupDescription: group.groupDescription ?? '',
      approvalStatus: group.approvalStatus,
      reviewerFirstName: this.normalizedReviewerName(),
    };
  }

  private applyLoadedView(view: RecruitGroupShareViewDto): void {
    this.applyShareView(view.group);
    this.inviteBriefTitle.set(view.inviteBriefTitle ?? this.inviteBriefTitle());
    this.invitedIds.set(new Set([
      ...(view.invitedCreatorIds ?? []),
      ...(this.group()?.invitedCreatorIds ?? []),
    ]));
  }

  private applyShareView(group: RecruitGroupResponseDto): void {
    const hydrated = hydrateRecruitGroupReview(group);
    this.group.set(hydrated);
    this.droppedIds.set(new Set(hydrated.droppedCreatorIds ?? []));
    this.keptIds.set(new Set(
      (hydrated.keptCreatorIds ?? []).filter((id) => !this.droppedIds().has(id)),
    ));
    this.creatorNotes.set(hydrated.creatorNotes ?? {});
  }

  private loadShare(token: string): void {
    if (!token) {
      this.loadingState.set('error');
      return;
    }

    this.loadingState.set('loading');
    this.reviewFilter.set('all');
    this.subscriptionList.add(
      this.recruitGroupService.getSharedGroup(token).subscribe({
        next: (view) => {
          this.brandName.set(view.brandName);
          this.applyLoadedView(view);
          this.creators.set(view.creators ?? []);
          this.reviewFilter.set(defaultCreatorReviewFilter(this.pendingCount()));
          this.loadingState.set('loaded');
        },
        error: () => {
          this.loadingState.set('error');
        },
      }),
    );
  }

  private loggedInFirstName(): string {
    const shoutUser = this.currentUserService.shoutUser as { firstName?: string } | null;
    return normalizeReviewerFirstName(shoutUser?.firstName);
  }

  private readStoredReviewerName(token: string): string {
    if (!token || typeof localStorage === 'undefined') {
      return '';
    }
    try {
      return normalizeReviewerFirstName(localStorage.getItem(this.reviewerStorageKey(token)) ?? '');
    } catch {
      return '';
    }
  }

  private storeReviewerName(token: string, value: string): void {
    if (!token || typeof localStorage === 'undefined') {
      return;
    }
    try {
      const name = normalizeReviewerFirstName(value);
      if (name) {
        localStorage.setItem(this.reviewerStorageKey(token), name);
      } else {
        localStorage.removeItem(this.reviewerStorageKey(token));
      }
    } catch {
      // Ignore private-mode storage failures.
    }
  }

  private reviewerStorageKey(token: string): string {
    return `${REVIEWER_NAME_STORAGE_PREFIX}${token}`;
  }

  private focusReviewerName(): void {
    queueMicrotask(() => {
      document.getElementById('reviewer-first-name')?.focus();
    });
  }
}
